import { Link, useMatchRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Bot,
  Search,
  Database,
  ChevronRight,
  LogOut,
  Radio,
  Goal,
  Activity,
  History
} from 'lucide-react';

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarSeparator,
  SidebarTrigger
} from '@/components/ui/sidebar';
import { useAuth } from '@/hooks/useAuth';
import { schemaApi, type ModelInfo } from '@/api/schema';

const CUSTOM_PAGES = [
  { label: 'Dashboard', to: '/', icon: LayoutDashboard },
  { label: 'Agents', to: '/custom/agents', icon: Bot },
  { label: 'Trades Explorer', to: '/custom/trades', icon: Search },
  { label: 'Courtside', to: '/custom/courtside', icon: Radio },
  { label: 'Courtside Monitor', to: '/custom/courtside-monitor', icon: Activity },
  { label: 'Courtside Review', to: '/custom/courtside-review', icon: History },
  { label: 'Sideline', to: '/custom/sideline', icon: Goal },
  { label: 'Sideline Monitor', to: '/custom/sideline-monitor', icon: Activity },
  { label: 'Sideline Review', to: '/custom/sideline-review', icon: History }
] as const;

const TABLE_LABELS: Record<string, string> = {
  agents: 'Agents',
  orders: 'Orders',
  trades: 'Trades',
  agents_performance_history: 'Performance History',
  llm_usage: 'LLM Usage'
};

export function AppSidebar() {
  const { isAuthenticated, logout } = useAuth();
  const matchRoute = useMatchRoute();

  const { data: schemaData } = useQuery({
    queryKey: ['schema'],
    queryFn: () => schemaApi.getSchema(),
    enabled: isAuthenticated,
    staleTime: Infinity
  });

  const tables: ModelInfo[] = schemaData?.data ?? [];

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  <span className="text-lg">📈</span>
                </div>
                <div className="flex flex-col gap-0.5 leading-none">
                  <span className="font-semibold">Backoffice</span>
                  <span className="text-xs text-muted-foreground">v2</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="overflow-hidden">
        <SidebarGroup>
          <SidebarGroupLabel>Pages</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {CUSTOM_PAGES.map(page => {
                const isActive = matchRoute({ to: page.to, fuzzy: false });
                return (
                  <SidebarMenuItem key={page.to}>
                    <SidebarMenuButton asChild isActive={!!isActive}>
                      <Link to={page.to}>
                        <page.icon className="size-4" />
                        <span>{page.label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator />

        <SidebarGroup>
          <SidebarGroupLabel>
            <Database className="mr-1 size-3" />
            Tables
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {tables.map(table => {
                const isActive = matchRoute({
                  to: '/tables/$tableName',
                  params: { tableName: table.name },
                  fuzzy: false
                });
                return (
                  <SidebarMenuItem key={table.name}>
                    <SidebarMenuButton asChild isActive={!!isActive}>
                      <Link to="/tables/$tableName" params={{ tableName: table.name }}>
                        <ChevronRight className="size-3" />
                        <span className="first-letter:uppercase">{TABLE_LABELS[table.name] ?? table.name}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarTrigger className="w-full justify-start gap-2 px-2 py-1.5 text-sm" />
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={logout}>
              <LogOut className="size-4" />
              <span>Logout</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
